import { useState, useRef } from "react";
import {
  getATMStates,
  getATMDistricts,
  searchATMs
} from "./Utility/util";

import "./ATMBranchLocator.css";

export default function ATMBranchLocator() {
  const [locatorType, setLocatorType] = useState("");

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [statesLoading, setStatesLoading] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [districtLoading, setDistrictLoading] = useState(false);

  const [branch, setBranch] = useState("");

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const resultRef = useRef(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleLocatorChange = async (e) => {
    const value = e.target.value;

    setLocatorType(value);
    setStates([]);
    setSelectedState("");
    setDistricts([]);
    setSelectedDistrict("");
    setBranch("");
    setResults([]);
    setCurrentPage(1);
    setTotalPages(1);
    if (value !== "ATM") return;

    setStatesLoading(true);
    try {
      const list = await getATMStates();
      setStates(list);
    } finally {
      setStatesLoading(false);
    }
  };


  const handleStateChange = async (e) => {
    const value = e.target.value;

    setSelectedState(value);
    setSelectedDistrict("");
    setBranch("");
    setResults([]);
    setDistricts([]);
    setCurrentPage(1);
    setTotalPages(1);
    if (!value) return;

    setDistrictLoading(true);
    try {
      const list = await getATMDistricts(value);
      setDistricts(list);
    } finally {
      setDistrictLoading(false);
    }
  };

  const fetchPage = async (page) => {
    if (loading) return;
    setLoading(true);
    try {
      const data = await searchATMs(
        selectedState,
        selectedDistrict,
        branch,
        page,
        pageSize
      );

      setResults(data.items || []);

      const totalCount = data.totalCount || 0;
      const pages = Math.ceil(totalCount / pageSize);

      setTotalPages(pages);
      setCurrentPage(page);


    } finally {
      setLoading(false);
      setTimeout(() => {
        resultRef.current?.focus();
      }, 300);
    }
  };

  const handleView = async () => {
    setHasSearched(true);

    if (!selectedState || !selectedDistrict || !branch) return;

    fetchPage(1);
  };





  return (
    <div className="atm-wrapper">
      <div className="atm-container">

        {/* ROW 1 */}
        <div className="atm-row">
          <div className="atm-field">
            <label htmlFor="atm-locator">
              ATM Locator <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>

            <select
              id="atm-locator"
              value={locatorType}
              onChange={handleLocatorChange}
              aria-required="true"
            >
              <option value="">Select</option>
              <option value="ATM">ATM Details</option>
            </select>
          </div>

          <div className="atm-field">
            <label htmlFor="state-select">
              State <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>
            {statesLoading ? (
              <div className="loading" role="status" aria-live="polite">Loading states…</div>
            ) : (
              <select
                id="state-select"
                aria-required="true"
                value={selectedState}
                onChange={handleStateChange}
                disabled={locatorType !== "ATM"}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* ROW 2 */}
        <div className="atm-row">
          <div className="atm-field">
            <label htmlFor="district-select">
              District <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>
            {districtLoading ? (
              <div className="loading" role="status" aria-live="polite">Loading districts…</div>
            ) : (
              <select
                id="district-select"
                aria-required="true"
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                disabled={!selectedState}
              >
                <option value="">Select District</option>
                {districts.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            )}
          </div>

          <div className="atm-field">
            <label htmlFor="atm-location">
              ATM Location <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </label>

            <select
              id="atm-location"
              aria-required="true"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              disabled={!selectedDistrict}
            >
              <option value="">Select Branch</option>
              <option value="All">All</option>
              {/* populate branch options if needed */}
            </select>
          </div>
        </div>

        {/* VIEW BUTTON */}
        <div className="atm-row">
          <button
            className="atm-search-btn"
            onClick={handleView}
            disabled={!selectedState || !selectedDistrict || !branch || loading}
            aria-busy={loading}
          >
            {loading ? "Loading..." : "View"}
          </button>
        </div>
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {!loading && results.length > 0 &&
            `${results.length} ATM records found`}
        </div>

        {/* RESULTS TABLE */}

        {results.length > 0 && (
          <div
            ref={resultRef}
            tabIndex="-1"
            role="region"
            aria-label="ATM Search Results"
          >
            <table className="atm-table">
              <caption className="sr-only">
                ATM Search Results
              </caption>
              <thead>
                <tr>
                  <th scope="col">ATM ID</th>
                  <th scope="col">ATM CR</th>
                  <th scope="col">Location</th>
                  <th scope="col">Address</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id}>
                    <td>{r.aTMID || "-"}</td>
                    <td>{r.aTMCR || "-"}</td>
                    <td>{r.lOCATION || "-"}</td>
                    <td>{r.address || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading &&
          hasSearched &&
          results.length === 0 && (
            <div role="status" aria-live="polite">
              No ATM records found.
            </div>
          )}
        {results.length > 0 && totalPages > 1 && (
          <div className="pagination">
            <button
              aria-label="Previous Page"
              disabled={currentPage === 1 || loading}
              onClick={() => fetchPage(currentPage - 1)}
            >
              Previous
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              aria-label="Next Page"
              disabled={currentPage === totalPages || loading}
              onClick={() => fetchPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}


      </div>
    </div>
  );
}
